import React, { useState, useEffect, useMemo } from 'react';
import './TableList.css';
import AthleteFormModal from './AthletesFormModal';
import LiveBalanceBar from './LiveBalanceBar';
import useLiveBalance from './useLiveBalance';
// helper robust pt. boolean (0/1, true/false, '1'/'0', 'true'/'false')
const toBool = (v) => v === true || v === 1 || v === '1' || v === 'true';

export default function AthletesList({ selectedYear, onYearChange, onListUpdate }) {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [sortBy, setSortBy] = useState('active-first');
  

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const token = useMemo(() => localStorage.getItem('jwtToken'), []);
  const {
    balance,
    loading: balanceLoading,
    prevBalance,
    refresh: refreshBalance
  } = useLiveBalance(selectedYear);


  const fetchAthletes = () => {
    setLoading(true);

    if (!token) {
      setError('Token JWT lipsă. Reautentifică-te.');
      setLoading(false);
      return;
    }

    fetch(`https://www.fro.ro/api/athletes/index.php?year=${selectedYear}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Eroare ${res.status}: ${text}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          const list = data.data || [];
          setAthletes(list);
          setError(null);
          onListUpdate?.(list);
        } else {
          setError(data.message || 'Eroare la încărcarea sportivilor');
        }
      })
      .catch((err) => {
        console.error('Eroare fetch sportivi:', err);
        setError(err.message || 'Eroare de conexiune');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAthletes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    onListUpdate?.(athletes);
  }, [athletes, onListUpdate]);

  const handleDelete = (athleteId) => {
    if (!window.confirm('Sigur dorești să ștergi acest sportiv?')) return;

    fetch(`https://www.fro.ro/api/athletes/delete.php?id=${athleteId}`, {
      method: 'DELETE',
      headers: { 'Authorization': 'Bearer ' + token },
    })
      .then(async (res) => {
        if (!res.ok) {
          const t = await res.text();
          throw new Error(`HTTP ${res.status}: ${t}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          setAthletes((prev) => prev.filter((r) => r.id !== athleteId));
        } else {
          alert('Eroare la ștergere: ' + (data.message || 'necunoscută'));
        }
      })
      .catch((err) => alert('Eroare la conexiune: ' + err.message));
  };

  const handleLegitimare = async (athlete) => {
    // ✅ permite doar anul curent și următorul
    if (selectedYear < currentYear || selectedYear > currentYear + 1) {
      alert(`Poți activa doar pentru anul curent (${currentYear}) sau următorul (${currentYear + 1}).`);
      return;
    }

    const confirm = window.confirm(
      `Vrei să activezi sportivul ${athlete.first_name} ${athlete.last_name} pentru ${selectedYear}?`
    );
    if (!confirm) return;

    try {
      const response = await fetch(
        'https://www.fro.ro/api/athletes/legitimeaza-sportiv.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
          },
          body: JSON.stringify({
            athlete_id: athlete.id,
            club_id: athlete.club_id,
            year: selectedYear,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        alert(`Activare efectuată. Taxa: ${data.taxa} lei.`);
        fetchAthletes();
        refreshBalance();
      } else {
        alert('Eroare: ' + (data.message || 'Nu s-a putut activa.'));
      }
    } catch (error) {
      alert('Eroare la conexiune: ' + error.message);
    }
  };

  const handleEdit = (athlete) => {
    setEditingAthlete(athlete);
    setShowAddModal(true);
  };

  // === Filtrare și sortare ===
  const filteredAthletes = athletes.filter((r) =>
    (`${r.first_name || ''} ${r.last_name || ''}`.toLowerCase()).includes(searchTerm.toLowerCase())
  );

  const sortedAthletes = [...filteredAthletes].sort((a, b) => {
    const activeA = toBool(a.is_legitimated);
    const activeB = toBool(b.is_legitimated);

    switch (sortBy) {
      case 'name-az': {
        const nameA = (`${a.last_name || ''} ${a.first_name || ''}`).toLowerCase();
        const nameB = (`${b.last_name || ''} ${b.first_name || ''}`).toLowerCase();
        return nameA.localeCompare(nameB);
      }
      case 'birth-up':
        return (a.birth_year || 0) - (b.birth_year || 0);
      case 'birth-down':
        return (b.birth_year || 0) - (a.birth_year || 0);
      case 'active-first':
        return activeA === activeB ? 0 : activeA ? -1 : 1;
      case 'inactive-first':
        return activeA === activeB ? 0 : activeA ? 1 : -1;
      default:
        return 0;
    }
  });

  if (loading) return <p>Se încarcă sportivii...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div className="table-list-container">
      <div className="table-list-header">
        <LiveBalanceBar
          amount={balance}
          loading={balanceLoading}
          prevAmount={prevBalance}
        />
        <div className="sort-bar">
          <label htmlFor="sort-select" className="sort-label">Sortare:</label>
          <select
            id="sort-select"
            className="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name-az">A–Z după nume</option>
            <option value="birth-up">An naștere ↑</option>
            <option value="birth-down">An naștere ↓</option>
            <option value="active-first">Activi întâi</option>
            <option value="inactive-first">Inactivi întâi</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <input
            type="text"
            className="search-bar"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            className="add-circle-btn"
            onClick={() => {
              setEditingAthlete(null);
              setShowAddModal(true);
            }}
            aria-label="Adaugă sportiv"
          >
            <span className="plus-icon">+</span>
          </button>
        </div>

        {/* ✅ Selector an: doar curent + următor */}
        <div className="year-selector">
          <label className="year-label">An:</label>
          <select
            className="year-dropdown"
            value={selectedYear}
            onChange={(e) => onYearChange?.(Number(e.target.value))}
          >
            {[currentYear, currentYear + 1].map((y) => (
              <option key={y} value={y}>
                {y === currentYear ? `${y} (curent)` : `${y} (următor)`}
              </option>
            ))}
          </select>
        </div>
      </div>

      <table className="table-list">
        <thead>
          <tr>
            <th>Nr. legitimație</th>
            <th>Nume</th>
            <th>An naștere</th>
            <th>CNP</th>
            <th>Email</th>
            <th>Telefon</th>
            <th>Activat</th>
            <th>Acțiuni</th>
          </tr>
        </thead>

        <tbody>
          {sortedAthletes.length === 0 ? (
            <tr><td colSpan={8}>Nu există sportivi.</td></tr>
          ) : (
            sortedAthletes.map((athlete) => (
              <tr
                key={athlete.id}
                className={toBool(athlete.is_legitimated) ? 'active-row' : ''}
                onClick={() => setSelectedId(athlete.id)}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  {athlete.nr_legitimatie && athlete.nr_legitimatie.trim() !== '' ? (
                    <span className="badge-legitimatie">{athlete.nr_legitimatie}</span>
                  ) : (
                    <span className="badge-legitimatie empty">-</span>
                  )}
                </td>
                
                <td>{athlete.first_name} {athlete.last_name}</td>
                <td>{athlete.birth_year}</td>
                <td>
                  {athlete.is_foreign_cnp === 1 ? (
                    <>
                      <span className="badge-country">
                        {athlete.foreign_country}
                      </span>
                    </>
                  ) : (
                    athlete.cnp || '-'
                  )}
                </td>

                <td>{athlete.email || '-'}</td>
                <td>{athlete.phone || '-'}</td>

                <td>
                  <input
                    type="checkbox"
                    checked={toBool(athlete.is_legitimated)}
                    disabled={toBool(athlete.is_legitimated) || selectedYear < currentYear - 1}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleLegitimare(athlete);
                    }}
                  />
                </td>

                <td className="table-list-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(athlete);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(athlete.id);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {showAddModal && (
        <AthleteFormModal
          onClose={() => setShowAddModal(false)}
          onAthleteAdded={fetchAthletes}
          editData={editingAthlete}
        />
      )}
    </div>
  );
}
